<template>
  <button
    :type="type"
    :class="buttonClasses"
    :disabled="disabled || loading"
    :title="title"
    @click="handleClick"
  >
    <svg
      v-if="loading"
      :class="['animate-spin', iconOnly ? '' : '-ml-1 mr-2', iconSizeClass]"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        class="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        stroke-width="4"
      />
      <path
        class="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>

    <template v-if="iconOnly && !loading">
      <slot />
    </template>

    <template v-else-if="!loading">
      <span v-if="$slots.iconLeft || $slots.prefix" class="mr-2">
        <slot name="iconLeft">
          <slot name="prefix" />
        </slot>
      </span>

      <slot />

      <span v-if="text">{{ text }}</span>

      <span v-if="$slots.iconRight || $slots.suffix" class="ml-2">
        <slot name="iconRight">
          <slot name="suffix" />
        </slot>
      </span>
    </template>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'outline-soft'
  | 'ghost'
  | 'danger'
  | 'danger-outline'
  | 'success'
  | 'icon-action'

export type ButtonSize = 'sm' | 'md' | 'lg'

const props = withDefaults(
  defineProps<{
    text?: string
    variant?: ButtonVariant
    size?: ButtonSize
    disabled?: boolean
    loading?: boolean
    block?: boolean
    type?: 'button' | 'submit' | 'reset'
    /** Icon-only mode (for icon buttons without text) */
    iconOnly?: boolean
    /** Tooltip/title for accessibility */
    title?: string
  }>(),
  {
    variant: 'primary',
    size: 'md',
    disabled: false,
    loading: false,
    block: false,
    type: 'button',
    iconOnly: false,
  },
)

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void
}>()

const handleClick = (event: MouseEvent) => {
  if (!props.disabled && !props.loading) {
    emit('click', event)
  }
}

// Base classes sempre aplicadas
const baseClasses =
  'inline-flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-lg'

// Classes por variante
const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-white font-normal hover:bg-secondary focus:ring-primary shadow-md',
  secondary:
    'bg-white text-default-black font-normal border border-border-gray hover:bg-grey-50 focus:ring-grey-200 shadow-sm',
  outline:
    'bg-transparent text-primary font-normal border border-primary hover:bg-royal-25 focus:ring-primary',
  'outline-soft':
    'bg-transparent text-royal-500 font-medium border border-royal-300 hover:bg-royal-25 focus:ring-royal-300',
  ghost:
    'bg-transparent text-primary font-normal hover:bg-royal-25 focus:ring-primary',
  danger:
    'bg-red-500 text-white font-normal hover:bg-red-600 focus:ring-red-500 shadow-md',
  'danger-outline':
    'bg-transparent text-error font-normal border border-error hover:bg-red-25 focus:ring-error',
  success:
    'bg-green-500 text-white font-normal hover:bg-green-600 focus:ring-green-500 shadow-md',
  'icon-action':
    'bg-white text-default-black font-normal border border-grey-100 hover:bg-grey-50 focus:ring-grey-200',
}

// Classes por tamanho
const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 py-2 text-sm min-h-[36px]',
  md: 'px-4 py-3 text-base min-h-[44px]',
  lg: 'px-6 py-4 text-lg min-h-[52px]',
}

// Classes de tamanho para icon-only
const iconOnlySizeClasses: Record<ButtonSize, string> = {
  sm: 'p-1.5 min-h-[32px] min-w-[32px]',
  md: 'p-2 min-h-[40px] min-w-[40px]',
  lg: 'p-2.5 min-h-[48px] min-w-[48px]',
}

// Tamanho do ícone baseado no size
const iconSizeClasses: Record<ButtonSize, string> = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
}

const iconSizeClass = computed(() => iconSizeClasses[props.size])

// Classes quando desabilitado
const disabledClasses = 'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none'

// Classes quando block
const blockClasses = computed(() => (props.block ? 'w-full' : ''))

// Combina todas as classes
const buttonClasses = computed(() => {
  const currentSizeClasses = props.iconOnly 
    ? iconOnlySizeClasses[props.size] 
    : sizeClasses[props.size]
    
  return [
    baseClasses,
    variantClasses[props.variant],
    currentSizeClasses,
    disabledClasses,
    blockClasses.value,
  ].join(' ')
})
</script>

